import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../../components/base/PageLayout';
const Mindset: React.FC = () => {
  React.useEffect(() => {
    document.title = 'おもてなしの心構え - 執事が大切にする相手本位のサービス精神｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが20年以上のキャリアで大切にしてきた「おもてなしの心構え」。相手本位の思考、先回りの気配り、見えないサービスの価値、謙虚さと誇りの両立など、真のホスピタリティを実現するための精神性を深く掘り下げます。テクニックだけでは到達できない、心からのおもてなしを提供するための執事の哲学と実践的なアドバイスをお届けします。');
    }
    
    // Add canonical tag
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://butlerbell.jp/wisdom/articles/mindset');
    
    // Add keywords meta tag
    let keywords = document.querySelector('meta[name="keywords"]');
    if (!keywords) {
      keywords = document.createElement('meta');
      keywords.setAttribute('name', 'keywords');
      document.head.appendChild(keywords);
    }
    keywords.setAttribute('content', 'おもてなし,心構え,ホスピタリティ,サービス精神,執事,マインドセット,接客,気配り');
  }, []);


  return (
    <PageLayout hero={<>
<div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: `url('/images/generated/seq-mindset-hero-7f1450.jpg')`
            }}
          />
          <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-serif mb-6 tracking-wide">
              おもてなしの心構え
            </h1>
            <p className="text-xl md:text-2xl font-light italic opacity-90">
              The Mindset of True Hospitality
            </p>
          </div>
        </div>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-600">
              <p className="text-lg leading-relaxed text-gray-800 mb-0">
                真のおもてなしとは、技術や知識だけでは成し得ません。それは心の奥底から湧き上がる、相手への深い思いやりと敬意に基づいた行動なのです。執事として長年培ってきた経験から、おもてなしの本質的な心構えについてお話しいたします。
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  相手の立場に立つということ
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      おもてなしの第一歩は、相手の立場に立って物事を考えることです。お客様が何を求めているのか、どのような気持ちでいらっしゃるのかを察知する能力が重要です。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      これは単なる想像ではありません。相手の表情、声のトーン、身振り手振りから読み取れる微細な変化に注意を払い、その瞬間に最も適切な対応を選択する技術なのです。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-mindset-empathy-01503b.jpg"
                      alt="相手の立場に立つ執事"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  期待を超える喜びの創造
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  真のおもてなしは、お客様の期待に応えるだけでは不十分です。期待を上回る体験を提供することで、心に残る感動を生み出すのです。
                </p>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">期待を超えるための3つの要素</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">1</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">先読みの心</h4>
                        <p className="text-gray-700">お客様が言葉にする前に、必要なものを察知し準備する</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">2</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">個別対応の技術</h4>
                        <p className="text-gray-700">一人ひとりの好みや特性に合わせたパーソナライズされたサービス</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">3</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">感動の演出</h4>
                        <p className="text-gray-700">小さなサプライズや心遣いで、特別な瞬間を創り出す</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  謙虚さと誇りの両立
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-mindset-humility-3779f2.jpg"
                      alt="謙虚さと誇りを持つ執事"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      執事として最も大切な心構えの一つは、謙虚さと職業への誇りを同時に持つことです。お客様に対しては常に謙虚でありながら、自分の仕事に対しては深い誇りを持つ。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      この絶妙なバランスが、真のプロフェッショナルとしての品格を生み出します。決して卑屈になることなく、しかし傲慢にもならない。この中庸の精神こそが、おもてなしの心の核心なのです。
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  継続的な学びと成長
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  おもてなしの心は、一度身につければ完成というものではありません。日々の経験を通じて磨き続け、常に向上を目指す姿勢が重要です。
                </p>
                
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">日々の実践ポイント</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <i className="ri-heart-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">相手の気持ちを第一に考える</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-eye-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">細部への注意を怠らない</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-time-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">適切なタイミングを見極める</span>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <i className="ri-book-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">常に学び続ける姿勢を持つ</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-team-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">チーム全体での連携を大切にする</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-star-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">自分自身の品格を磨く</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  心からの笑顔の力
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  技術的な完璧さも重要ですが、最終的にお客様の心を動かすのは、心からの笑顔と温かい人間性です。作られた笑顔ではなく、相手の幸せを願う気持ちから自然に生まれる表情こそが、真のおもてなしの象徴なのです。
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    執事ベルからのアドバイス
                  </h3>
                  <p className="text-amber-700 leading-relaxed">
                    「おもてなしの心は、技術を超えた人間の本質的な美しさの表現です。相手を思いやる気持ちを大切にし、その気持ちを行動で示すことから始めてください。完璧を目指しながらも、人間らしい温かさを忘れないことが、真のプロフェッショナルへの道なのです。」
                  </p>
                </div>
              </section>
            </div>

            {/* Related Articles */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-2xl font-serif text-gray-900 mb-8">関連記事</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/wisdom/articles/vip-service" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-vip-36d964.jpg"
                    alt="VIP対応の極意"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">VIP対応の極意</h4>
                    <p className="text-sm text-gray-600 mt-2">特別なお客様への最高級のサービス提供方法</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/communication" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-comm-b51980.jpg"
                    alt="コミュニケーション術"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">コミュニケーション術</h4>
                    <p className="text-sm text-gray-600 mt-2">心に響く会話と信頼関係の築き方</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/observation" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-obs-a97642.jpg"
                    alt="観察力の磨き方"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">観察力の磨き方</h4>
                    <p className="text-sm text-gray-600 mt-2">お客様のニーズを察知する技術</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Mindset;
